/**
 * Fraud Detection Dashboard
 * 
 * Phase 9.0: Monitor fraud flags, block/unblock players, view statistics
 */

import { useState, useEffect, useCallback } from "react";
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Button,
  Spinner,
  DataTable,
  Badge,
  Select,
  TextField,
  Popover,
  ActionList,
} from "@shopify/polaris";
import { getFraudStats, getFraudFlags, blockPlayer, unblockPlayer, type FraudFlag, type FraudStats } from "../api/fraud";

function FraudDetectionPage() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<FraudStats | null>(null);
  const [flags, setFlags] = useState<FraudFlag[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [severityFilter, setSeverityFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const limit = 20;

  // Action states
  const [actioningPlayerId, setActioningPlayerId] = useState<string | null>(null);
  const [popoverActive, setPopoverActive] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, flagsData] = await Promise.all([
        getFraudStats(),
        getFraudFlags({
          type: typeFilter || undefined,
          severity: severityFilter ? parseInt(severityFilter) : undefined,
          limit,
          offset: (page - 1) * limit,
        }),
      ]);

      setStats(statsData);
      setFlags(flagsData.flags);
      setTotal(flagsData.total);
    } catch (err) {
      console.error("[Fraud] Fetch error:", err);
      setError(err instanceof Error ? err.message : "Failed to load fraud data");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, severityFilter, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBlockPlayer = async (playerId: string) => {
    if (!confirm("Are you sure you want to block this player? They will not be able to play any games.")) {
      return;
    }

    try {
      setActioningPlayerId(playerId);
      await blockPlayer(playerId, "Blocked by merchant via fraud dashboard");
      await fetchData(); // Refresh data
    } catch (err) {
      alert(`Failed to block player: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setActioningPlayerId(null);
      setPopoverActive(null);
    }
  };

  const handleUnblockPlayer = async (playerId: string) => {
    try {
      setActioningPlayerId(playerId);
      await unblockPlayer(playerId);
      await fetchData(); // Refresh data
    } catch (err) {
      alert(`Failed to unblock player: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setActioningPlayerId(null);
      setPopoverActive(null);
    }
  };

  const getSeverityBadge = (severity: number) => {
    if (severity >= 8) return <Badge tone="critical">Critical</Badge>;
    if (severity >= 5) return <Badge tone="warning">High</Badge>;
    if (severity >= 3) return <Badge tone="attention">Medium</Badge>;
    return <Badge tone="info">Low</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Build table rows
  const tableRows = flags.map((flag) => [
    formatDate(flag.createdAt),
    flag.type,
    getSeverityBadge(flag.severity),
    flag.player?.fingerprint?.substring(0, 8) + "..." || "Unknown",
    flag.player?.isBlocked ? <Badge tone="critical">Blocked</Badge> : <Badge tone="success">Active</Badge>,
    flag.player ? (
      <Popover
        active={popoverActive === flag.id}
        activator={
          <Button
            size="slim"
            onClick={() => setPopoverActive(popoverActive === flag.id ? null : flag.id)}
            loading={actioningPlayerId === flag.player?.id}
          >
            Actions
          </Button>
        }
        onClose={() => setPopoverActive(null)}
      >
        <ActionList
          items={[
            {
              content: flag.player.isBlocked ? "Unblock Player" : "Block Player",
              onAction: () =>
                flag.player!.isBlocked
                  ? handleUnblockPlayer(flag.player!.id)
                  : handleBlockPlayer(flag.player!.id),
              destructive: !flag.player.isBlocked,
            },
            {
              content: "View Player History",
              onAction: () => {
                // TODO: Navigate to player detail page
                alert(`Player ID: ${flag.player!.id}\nFingerprint: ${flag.player!.fingerprint}`);
              },
            },
          ]}
        />
      </Popover>
    ) : (
      <Text as="span" tone="subdued">
        N/A
      </Text>
    ),
  ]);

  return (
    <Page
      title="Fraud Detection"
      subtitle="Monitor suspicious activity and manage player access"
      backAction={{ content: "Settings", url: "/settings" }}
      primaryAction={{
        content: "Refresh",
        onAction: fetchData,
        loading,
      }}
    >
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical">
            <Text as="p">{error}</Text>
          </Banner>
        )}

        {loading && !stats && (
          <Card>
            <BlockStack gap="300" align="center">
              <Spinner size="large" />
              <Text as="p" tone="subdued">
                Loading fraud data...
              </Text>
            </BlockStack>
          </Card>
        )}

        {stats && (
          <>
            {/* Stats Cards */}
            <InlineStack gap="400" wrap>
              <Card>
                <BlockStack gap="300">
                  <Text as="h3" variant="headingMd">
                    Total Flags
                  </Text>
                  <Text as="p" variant="heading2xl" fontWeight="bold">
                    {stats.totalFlags.toLocaleString()}
                  </Text>
                  <Text as="p" tone="subdued">
                    All fraud flags recorded
                  </Text>
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="300">
                  <Text as="h3" variant="headingMd">
                    Blocked Players
                  </Text>
                  <Text as="p" variant="heading2xl" fontWeight="bold">
                    {stats.blockedPlayers.toLocaleString()}
                  </Text>
                  <Text as="p" tone="subdued">
                    Players currently blocked
                  </Text>
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="300">
                  <Text as="h3" variant="headingMd">
                    Last 24 Hours
                  </Text>
                  <Text as="p" variant="heading2xl" fontWeight="bold">
                    {stats.flagsLast24h.toLocaleString()}
                  </Text>
                  <Text as="p" tone="subdued">
                    Recent fraud flags
                  </Text>
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="300">
                  <Text as="h3" variant="headingMd">
                    Last 7 Days
                  </Text>
                  <Text as="p" variant="heading2xl" fontWeight="bold">
                    {stats.flagsLast7d.toLocaleString()}
                  </Text>
                  <Text as="p" tone="subdued">
                    Weekly trend
                  </Text>
                </BlockStack>
              </Card>
            </InlineStack>

            {/* Filters */}
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Filters
                </Text>
                <InlineStack gap="400">
                  <div style={{ width: "250px" }}>
                    <Select
                      label="Flag Type"
                      options={[
                        { label: "All Types", value: "" },
                        ...stats.flagsByType.map((f) => ({ label: `${f.type} (${f.count})`, value: f.type })),
                      ]}
                      value={typeFilter}
                      onChange={setTypeFilter}
                    />
                  </div>
                  <div style={{ width: "250px" }}>
                    <Select
                      label="Severity"
                      options={[
                        { label: "All Severities", value: "" },
                        { label: "Critical (8+)", value: "8" },
                        { label: "High (5-7)", value: "5" },
                        { label: "Medium (3-4)", value: "3" },
                        { label: "Low (1-2)", value: "1" },
                      ]}
                      value={severityFilter}
                      onChange={setSeverityFilter}
                    />
                  </div>
                </InlineStack>
              </BlockStack>
            </Card>

            {/* Fraud Flags Table */}
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    Fraud Flags ({total} total)
                  </Text>
                  {loading && <Spinner size="small" />}
                </InlineStack>

                {tableRows.length > 0 ? (
                  <DataTable
                    columnContentTypes={["text", "text", "text", "text", "text", "text"]}
                    headings={["Timestamp", "Type", "Severity", "Player", "Status", "Actions"]}
                    rows={tableRows}
                  />
                ) : (
                  <Text as="p" tone="subdued">
                    No fraud flags found. This is a good sign! 🎉
                  </Text>
                )}

                {/* Pagination */}
                {total > limit && (
                  <InlineStack align="center" gap="200">
                    <Button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Text as="p">
                      Page {page} of {Math.ceil(total / limit)}
                    </Text>
                    <Button
                      onClick={() => setPage(page + 1)}
                      disabled={page >= Math.ceil(total / limit)}
                    >
                      Next
                    </Button>
                  </InlineStack>
                )}
              </BlockStack>
            </Card>

            {/* Info Banner */}
            <Banner tone="info">
              <BlockStack gap="200">
                <Text as="p" fontWeight="semibold">
                  🛡️ Fraud Detection Types
                </Text>
                <Text as="p">
                  • <strong>RATE_LIMIT_IP:</strong> Too many plays from same IP address
                  <br />
                  • <strong>RATE_LIMIT_FINGERPRINT:</strong> Too many plays from same device
                  <br />
                  • <strong>SUSPICIOUS_PATTERN:</strong> Unusual play patterns detected
                  <br />
                  • <strong>MANUAL_BLOCK:</strong> Manually blocked by merchant
                </Text>
              </BlockStack>
            </Banner>
          </>
        )}
      </BlockStack>
    </Page>
  );
}

export default FraudDetectionPage;
